/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { Span } from '@opentelemetry/api'
import type { INPMetricWithAttribution } from 'web-vitals/attribution'

import { setNumberAttribute, setStringAttribute } from '../../utils/span-attributes'
import { WebVitalsAttributionOptions } from './types'

type INPLongestScriptEntry = NonNullable<INPMetricWithAttribution['attribution']['longestScript']>['entry']

const LONGEST_SCRIPT_ATTRIBUTE_PREFIX = 'inp.longest_script'

export function setINPAttributionAttributes(
	span: Span,
	metric: INPMetricWithAttribution,
	options: WebVitalsAttributionOptions,
): void {
	const { attribution } = metric

	setStringAttribute(span, 'inp.interaction_target', options.getTarget(attribution.interactionTarget))

	setNumberAttribute(span, 'inp.interaction_time', attribution.interactionTime)
	setStringAttribute(span, 'inp.interaction_type', attribution.interactionType)
	setNumberAttribute(span, 'inp.next_paint_time', attribution.nextPaintTime)
	setNumberAttribute(span, 'inp.input_delay', attribution.inputDelay)
	setNumberAttribute(span, 'inp.processing_duration', attribution.processingDuration)
	setNumberAttribute(span, 'inp.presentation_delay', attribution.presentationDelay)
	setStringAttribute(span, 'inp.load_state', attribution.loadState)
	setNumberAttribute(span, 'inp.total_script_duration', attribution.totalScriptDuration)
	setNumberAttribute(span, 'inp.total_style_and_layout_duration', attribution.totalStyleAndLayoutDuration)
	setNumberAttribute(span, 'inp.total_paint_duration', attribution.totalPaintDuration)
	setNumberAttribute(span, 'inp.total_unattributed_duration', attribution.totalUnattributedDuration)
	setStringAttribute(span, 'inp.longest_script_subpart', attribution.longestScript?.subpart)
	setNumberAttribute(
		span,
		'inp.longest_script_intersecting_duration',
		attribution.longestScript?.intersectingDuration,
	)

	const longestScript = attribution.longestScript?.entry
	if (!longestScript) {
		return
	}

	setINPLongestScriptAttributes(span, longestScript)
}

function setINPLongestScriptAttributes(span: Span, script: INPLongestScriptEntry): void {
	setNumberAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.duration`, script.duration)
	setNumberAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.execution_start`, script.executionStart)
	setStringAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.invoker`, script.invoker)
	setStringAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.invoker_type`, script.invokerType)
	setNumberAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.pause_duration`, script.pauseDuration)
	setNumberAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.source_char_position`, script.sourceCharPosition)
	setStringAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.source_function_name`, script.sourceFunctionName)
	setStringAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.source_url`, script.sourceURL)
	setNumberAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.start_time`, script.startTime)
	setStringAttribute(span, `${LONGEST_SCRIPT_ATTRIBUTE_PREFIX}.window_attribution`, script.windowAttribution)
}
